import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client, { whatsappLink, formatINR } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { Heart, MessageCircle, Phone, Calendar, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import MakingChargeCalculator from "@/components/MakingChargeCalculator";
import ProductCard from "@/components/ProductCard";

export default function ProductDetail() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [related, setRelated] = useState([]);
    const [activeImg, setActiveImg] = useState(0);
    const { settings } = useSettings();
    const { user } = useAuth();

    useEffect(() => {
        client.get(`/products/${id}`).then(r => {
            setProduct(r.data);
            // Fetch related in same category
            client.get(`/products?category=${r.data.category}&limit=8`).then(rr => {
                setRelated(rr.data.filter(p => p.id !== r.data.id).slice(0, 4));
            });
        }).catch(() => setProduct(null));
        window.scrollTo(0, 0);
    }, [id]);

    if (!product) return <div className="max-w-[1440px] mx-auto px-6 py-24 text-center font-serif-luxe text-xl">Loading…</div>;

    const restricted = product.access_level === "vip" && !user;

    if (restricted) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                <Lock size={32} className="mx-auto text-[color:var(--champagne)] mb-4" />
                <div className="eyebrow">Exclusive Collection</div>
                <h1 className="font-serif-luxe text-4xl mt-3">Login to view this collection</h1>
                <p className="text-[color:var(--muted-brown)] mt-4 mb-8">Create an account to explore our exclusive jewellery designs, curated only for our valued patrons.</p>
                <div className="flex gap-3 justify-center">
                    <Link to="/login" className="btn-primary">Login</Link>
                    <Link to="/register" className="btn-outline">Create Account</Link>
                </div>
            </div>
        );
    }

    const priceDisplay = product.price_visibility === "visible" && product.price != null ? formatINR(product.price) : "Price on Request";

    const wa = whatsappLink(
        settings.whatsapp_number,
        `Hello Singh Jewellers,\n\nI am interested in:\nProduct: ${product.name}\nProduct Code: ${product.sku}\n\nPlease share availability, price and details.`
    );

    const addWish = async () => {
        if (!user) return toast.error("Please login to save favourites");
        try {
            await client.post(`/wishlist/${product.id}`);
            toast.success("Added to wishlist");
        } catch { toast.error("Could not add"); }
    };

    return (
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 lg:py-16">
            <div className="text-xs text-[color:var(--muted-brown)] mb-6 flex flex-wrap gap-2">
                <Link to="/" className="hover:text-[color:var(--champagne)]">Home</Link> / <Link to={`/${product.category}-jewellery`} className="hover:text-[color:var(--champagne)] capitalize">{product.category}</Link> / <span className="text-[color:var(--charcoal)]">{product.name}</span>
            </div>

            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
                <div>
                    <div className="product-img-wrap aspect-[4/5] mb-4" onContextMenu={(e) => e.preventDefault()} data-testid="product-main-image">
                        <img src={product.images?.[activeImg]} alt={product.name} draggable="false" className="w-full h-full object-cover" />
                        <div className="watermark-diag"><img src="/logo-watermark.png" alt="Singh Jewellers" /></div>
                        <img className="watermark" src="/logo-watermark.png" alt="Singh Jewellers" />
                    </div>
                    {product.images?.length > 1 && (
                        <div className="grid grid-cols-4 gap-3">
                            {product.images.map((img, i) => (
                                <button key={i} onClick={() => setActiveImg(i)} className={`product-img-wrap aspect-square border-2 ${activeImg === i ? "border-[color:var(--champagne)]" : "border-transparent"}`} data-testid={`thumb-${i}`}>
                                    <img src={img} alt={`${product.name} ${i}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <div className="eyebrow">{product.category} · {product.collection}</div>
                    <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3 mb-3" data-testid="product-title">{product.name}</h1>
                    <div className="text-xs tracking-[0.22em] text-[color:var(--muted-brown)] mb-6">CODE · {product.sku}</div>

                    <div className="font-serif-luxe text-3xl text-[color:var(--charcoal)] mb-2" data-testid="product-price">{priceDisplay}</div>
                    {product.making_charge && <div className="text-xs text-[color:var(--muted-brown)] mb-6">Making charge: {formatINR(product.making_charge)}</div>}

                    <div className="divider-line my-6" />

                    <div className="grid grid-cols-2 gap-5 mb-8 text-sm">
                        <div><div className="eyebrow text-[color:var(--muted-brown)]">Metal</div><div className="mt-1">{product.metal}</div></div>
                        <div><div className="eyebrow text-[color:var(--muted-brown)]">Purity</div><div className="mt-1">{product.purity}</div></div>
                        {product.gross_weight && <div><div className="eyebrow text-[color:var(--muted-brown)]">Gross Weight</div><div className="mt-1">{product.gross_weight} g</div></div>}
                        {product.net_weight && <div><div className="eyebrow text-[color:var(--muted-brown)]">Net Weight</div><div className="mt-1">{product.net_weight} g</div></div>}
                        {product.gemstone && <div><div className="eyebrow text-[color:var(--muted-brown)]">Stone</div><div className="mt-1">{product.gemstone}</div></div>}
                        <div><div className="eyebrow text-[color:var(--muted-brown)]">Availability</div><div className="mt-1 text-[color:var(--champagne)]">In Stock</div></div>
                    </div>

                    <p className="text-sm text-[color:var(--muted-brown)] leading-relaxed mb-8">{product.description}</p>

                    <div className="flex flex-wrap gap-3 mb-8">
                        <a href={wa} target="_blank" rel="noreferrer" className="btn-whatsapp" data-testid="whatsapp-enquire"><MessageCircle size={16} /> WhatsApp Enquiry</a>
                        <a href={`tel:${settings.phone}`} className="btn-primary" data-testid="call-now"><Phone size={16} /> Call Now</a>
                        <Link to="/contact" className="btn-outline"><Calendar size={16} /> Book Visit</Link>
                        <button onClick={addWish} className="btn-outline" data-testid="add-wishlist"><Heart size={16} /> Wishlist</button>
                    </div>

                    <div className="border border-[color:var(--soft-border)] p-5 bg-[color:var(--beige)]/40">
                        <div className="flex items-center gap-2 eyebrow"><ShieldCheck size={14} /> Singh Jewellers Assurance</div>
                        <div className="text-xs text-[color:var(--muted-brown)] mt-2 leading-relaxed">
                            Every piece from Singh Jewellers reflects our commitment to purity, honest making charges and considered craftsmanship. Confirm final rate at the showroom.
                        </div>
                    </div>
                </div>
            </div>

            {(product.category === "gold" || product.category === "bridal") && product.purity && product.gross_weight && (
                <div className="mt-16 lg:mt-24 max-w-3xl mx-auto">
                    <MakingChargeCalculator
                        defaultPurity={product.purity}
                        defaultWeight={product.gross_weight}
                        defaultMakingPct={product.making_charge && product.price ? Math.round((product.making_charge / product.price) * 100) : 8}
                        productName={product.name}
                    />
                </div>
            )}

            {related.length > 0 && (
                <section className="mt-20 lg:mt-28" data-testid="related-products">
                    <div className="text-center mb-10">
                        <div className="eyebrow">Discover More</div>
                        <h2 className="font-serif-luxe text-3xl md:text-4xl mt-3">You May Also Like</h2>
                        <div className="w-14 h-[1px] bg-[color:var(--champagne)] mx-auto mt-5" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-7">
                        {related.map(p => <ProductCard key={p.id} product={p} />)}
                    </div>
                </section>
            )}
        </div>
    );
}
